import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import Layout from '../../components/ui/Layout';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { IconCheck, IconX, IconArrowsExchange, IconChevronDown, IconChevronRight } from '@tabler/icons-react';

const STATUS_TABS = [
  { code: 'pending',  label: 'Pending',  color: 'var(--color-warning)' },
  { code: 'approved', label: 'Approved', color: 'var(--color-success)' },
  { code: 'rejected', label: 'Rejected', color: 'var(--color-danger)' },
  { code: 'merged',   label: 'Merged',   color: 'var(--color-info)' },
  { code: 'all',      label: 'All',      color: 'var(--color-text-secondary)' },
];

const TYPE_LABELS = {
  make: 'Make',
  model: 'Model',
  subCategory: 'Sub-category',
  serviceInterval: 'Service interval',
  document: 'Document',
};

export default function LibraryRequests() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [acting, setActing] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'libraryRequests'), orderBy('submittedAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const filtered = requests.filter(r => statusFilter === 'all' || r.status === statusFilter);

  const counts = STATUS_TABS.reduce((acc, t) => {
    acc[t.code] = t.code === 'all' ? requests.length : requests.filter(r => r.status === t.code).length;
    return acc;
  }, {});

  const callFn = async (fnName, data) => {
    setActing(data.requestId); setError(null);
    try {
      const fn = httpsCallable(functions, fnName);
      await fn(data);
    } catch (err) {
      setError({ requestId: data.requestId, message: err.message });
    }
    setActing(null);
  };

  const approve = (req) => {
    if (!window.confirm('Approve this submission? It will be added to the canonical library.')) return;
    callFn('approveLibraryRequest', { requestId: req.id });
  };

  const reject = (req) => {
    const reason = window.prompt('Rejection reason (optional):') || '';
    if (reason === null) return;
    callFn('rejectLibraryRequest', { requestId: req.id, rejectionReason: reason });
  };

  const merge = (req) => {
    if (!req.fuzzyMatchedCanonicalId) {
      alert('No fuzzy match found to merge with. Use approve to create a new canonical record, or reject.');
      return;
    }
    if (!window.confirm('Merge this submission with the existing canonical record? The submission will be marked as merged.')) return;
    callFn('mergeLibraryRequest', { requestId: req.id, mergeWithCanonicalId: req.fuzzyMatchedCanonicalId });
  };

  return (
    <Layout>
      <PageHeader
        title="Library requests"
        subtitle={`${counts.pending} pending · ${counts.all} total`}
      />
      <div style={{ padding: 28 }}>
        {/* Status tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(tab => {
            const active = statusFilter === tab.code;
            return (
              <div key={tab.code} onClick={() => setStatusFilter(tab.code)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 'var(--radius-full)',
                  background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)',
                  color: active ? '#fff' : 'var(--color-text-secondary)',
                  border: active ? 'none' : '0.5px solid var(--color-border-light)',
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {tab.label}
                <span style={{ fontSize: 10, opacity: active ? 0.85 : 0.5 }}>{counts[tab.code]}</span>
              </div>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <Card>
            <div style={{ padding: '40px 20px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              No {statusFilter === 'all' ? '' : statusFilter} requests.
            </div>
          </Card>
        ) : (
          <Card padding={0}>
            {filtered.map((req, i) => (
              <RequestRow
                key={req.id}
                req={req}
                isLast={i === filtered.length - 1}
                expanded={expandedId === req.id}
                onToggle={() => setExpandedId(expandedId === req.id ? null : req.id)}
                onApprove={() => approve(req)}
                onReject={() => reject(req)}
                onMerge={() => merge(req)}
                acting={acting === req.id}
                error={error?.requestId === req.id ? error.message : null}
              />
            ))}
          </Card>
        )}
      </div>
    </Layout>
  );
}

function RequestRow({ req, isLast, expanded, onToggle, onApprove, onReject, onMerge, acting, error }) {
  const isPending = req.status === 'pending';
  const submittedAt = req.submittedAt?.toDate?.()?.toLocaleString() || '—';
  const summary = req.data?.name || req.data?.documentType || 'Untitled';

  return (
    <div style={{ borderBottom: isLast ? 'none' : '0.5px solid var(--color-border-light)' }}>
      <div onClick={onToggle} style={{ display: 'grid', gridTemplateColumns: '24px 110px 1fr 180px 130px 90px', padding: '12px 16px', gap: 12, alignItems: 'center', cursor: 'pointer', fontSize: 12 }}>
        <div style={{ color: 'var(--color-text-tertiary)' }}>
          {expanded ? <IconChevronDown size={14} stroke={1.5} /> : <IconChevronRight size={14} stroke={1.5} />}
        </div>
        <div>
          <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {TYPE_LABELS[req.type] || req.type}
          </span>
        </div>
        <div style={{ fontWeight: 500 }}>{summary}</div>
        <div style={{ color: 'var(--color-text-tertiary)' }}>{req.submittedByEmail || '—'}</div>
        <div style={{ color: 'var(--color-text-tertiary)', fontSize: 11 }}>{submittedAt}</div>
        <StatusBadge status={req.status} />
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 16px 52px', fontSize: 12 }}>
          <div style={{ background: 'var(--color-bg-secondary)', padding: 12, borderRadius: 'var(--radius-md)', marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Submitted data</div>
            <pre style={{ fontFamily: 'var(--font-mono)', fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(req.data, null, 2)}
            </pre>
          </div>

          {req.fuzzyMatchedCanonicalId && (
            <div style={{ padding: '8px 12px', background: 'var(--color-info-light)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12, color: 'var(--color-info)' }}>
              Fuzzy match found with existing canonical record: <code style={{ fontFamily: 'var(--font-mono)' }}>{req.fuzzyMatchedCanonicalId}</code>
            </div>
          )}

          {req.similarRequestIds?.length > 0 && (
            <div style={{ padding: '8px 12px', background: 'var(--color-warning-light)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12, color: 'var(--color-warning)' }}>
              {req.similarRequestIds.length} similar pending submission(s) — consider reviewing them together.
            </div>
          )}

          {req.rejectionReason && (
            <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12, color: 'var(--color-danger)' }}>
              Rejection reason: {req.rejectionReason}
            </div>
          )}

          {req.resultingCanonicalId && (
            <div style={{ padding: '8px 12px', background: 'var(--color-success-light)', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12, color: 'var(--color-success)' }}>
              Canonical ID: <code style={{ fontFamily: 'var(--font-mono)' }}>{req.resultingCanonicalId}</code>
            </div>
          )}

          {isPending && (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" loading={acting} onClick={onApprove} icon={<IconCheck size={12} stroke={2} />}>Approve</Button>
              {req.fuzzyMatchedCanonicalId && (
                <Button size="sm" variant="secondary" loading={acting} onClick={onMerge} icon={<IconArrowsExchange size={12} stroke={2} />}>Merge with existing</Button>
              )}
              <Button size="sm" variant="secondary" loading={acting} onClick={onReject} icon={<IconX size={12} stroke={2} />}>Reject</Button>
            </div>
          )}

          {error && (
            <div style={{ padding: '8px 12px', background: 'var(--color-danger-light)', color: 'var(--color-danger)', borderRadius: 'var(--radius-md)', fontSize: 12, marginTop: 12 }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending:  { background: 'var(--color-warning-light)', color: 'var(--color-warning)' },
    approved: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
    rejected: { background: 'var(--color-danger-light)',  color: 'var(--color-danger)' },
    merged:   { background: 'var(--color-info-light)',    color: 'var(--color-info)' },
  };
  const style = styles[status] || { background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' };
  return (
    <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 'var(--radius-full)', textTransform: 'uppercase', letterSpacing: '0.04em', ...style }}>
      {status}
    </span>
  );
}
